import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from "react";
import { PLAY, PROPOSED } from "../design/copy.js";
import { Lettering, NotYet } from "./primitives.js";
import { useDictation, type DictationControl, type TranscribeFn } from "./dictation.js";

/**
 * The one free-text field. Frames 6, 8 and 11 are the same component with a
 * different placeholder; onboarding and play do not get separate inputs.
 *
 * KEYBOARD CONTRACT — Enter makes a NEW LINE; the send button submits.
 *
 * This is the inverse of the chat convention, deliberately. In a messenger,
 * Enter-to-send is right because a message is cheap and a wrong one is
 * retractable. Here a turn spends real money, burns an irreversible day in the
 * fiction, and cannot be taken back — and the field is asking for a paragraph of
 * reasoning, not a line of chat. Enter-to-send in a box people are meant to
 * think in sends half-finished thoughts, and the cost of that is not a typo, it
 * is a day of the engagement.
 *
 * Cmd/Ctrl+Enter still submits, for people who have built the habit.
 *
 * Up recalls history from an empty field; `/` focuses from anywhere (registered
 * on the document, so it works while the learner is reading something).
 */
export interface InputProps {
  readonly placeholder: string;
  readonly disabled?: boolean | undefined;
  readonly disabledReason?: string | undefined;
  readonly size?: "lede" | "small" | undefined;
  readonly extraControl?: ReactNode | undefined;
  readonly onSubmit: (text: string) => void;
  readonly onOpenSettings: () => void;
  /**
   * Draft persistence, for the surfaces that have somewhere to persist to.
   *
   * A long composed answer is worth more here than in a chat box: it is the
   * learner's actual thinking, it may have taken minutes, and a reload — a
   * refresh, a crash, a closed tab — currently destroys it silently. When these
   * are supplied the field mirrors its value out to the caller; when they are
   * not, it is an ordinary uncontrolled field.
   */
  readonly draft?: string | undefined;
  readonly onDraftChange?: ((text: string) => void) | undefined;
  /**
   * The microphone (ADR 0011). When supplied, the control is live: press to
   * listen, press again to finish, and what was heard is appended to the
   * draft — appended, never sent. Absent (no voice on this deployment, or a
   * static preview) the control shows as "not yet.", exactly as before.
   */
  readonly dictate?: TranscribeFn | null | undefined;
  /** Tighter chrome for a narrow column: smaller controls, no attach affordance. */
  readonly compact?: boolean | undefined;
  /** The field itself, for a caller that focuses it. */
  readonly fieldRef?: RefObject<HTMLTextAreaElement | null> | undefined;
}

export function FreeTextInput({
  placeholder,
  disabled = false,
  disabledReason,
  size = "small",
  extraControl,
  onSubmit,
  onOpenSettings,
  draft,
  onDraftChange,
  dictate,
  compact = false,
  fieldRef,
}: InputProps): ReactNode {
  const [ownValue, setOwnValue] = useState("");
  const [history, setHistory] = useState<readonly string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const own = useRef<HTMLTextAreaElement | null>(null);
  const ref = fieldRef ?? own;

  // Controlled by the caller when a draft store exists, uncontrolled otherwise.
  // One value, one setter, so nothing below has to care which it is.
  const controlled = draft !== undefined && onDraftChange !== undefined;
  const value = controlled ? draft : ownValue;
  const setValue = useCallback(
    (next: string) => {
      if (controlled) onDraftChange(next);
      else setOwnValue(next);
    },
    [controlled, onDraftChange],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "/") return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable === true) return;
      event.preventDefault();
      ref.current?.focus();
    };
    globalThis.document.addEventListener("keydown", onKey);
    return () => globalThis.document.removeEventListener("keydown", onKey);
  }, []);

  /**
   * Dictated words join the draft rather than replacing it, so a learner who
   * typed half a thought and then spoke the rest keeps both. The field takes
   * focus afterwards: the next thing they do is read it.
   */
  const appendDictation = useCallback(
    (text: string) => {
      const base = value.trimEnd();
      setValue(base.length === 0 ? text : `${base} ${text}`);
      ref.current?.focus();
    },
    [value, setValue],
  );
  const dictation = useDictation(dictate, appendDictation);

  const submit = useCallback(() => {
    const text = value.trim();
    if (text.length === 0 || disabled) return;
    setHistory((h) => [...h, text]);
    setHistoryIndex(null);
    setValue("");
    onSubmit(text);
  }, [value, disabled, onSubmit, setValue]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      // Cmd/Ctrl+Enter is the only key that sends. A bare Enter falls through
      // to the textarea and inserts a line break, which is the whole point.
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        submit();
        return;
      }
      if (event.key === "ArrowUp" && value.length === 0 && history.length > 0) {
        event.preventDefault();
        const next = historyIndex === null ? history.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(next);
        setValue(history[next] ?? "");
      }
    },
    [submit, value, history, historyIndex, setValue],
  );

  const textSize = size === "lede" ? "text-lede" : "text-small";
  const canSend = value.trim().length > 0 && !disabled;

  /*
   * THE FIELD GROWS WITH THE WRITING (2026-10-02), the way a field does: as
   * tall as its text up to the cap the stylesheet gives it (`.pc-field`),
   * and scrolling inside itself past that. Measured here rather than left to
   * `field-sizing: content`, which not every browser has yet — a field that
   * grows in one browser and clips in another is two products. The height is
   * set in the layout phase so the writer never sees a frame of the old one.
   */
  useLayoutEffect(() => {
    const field = ref.current;
    if (field === null) return;
    field.style.height = "auto";
    field.style.height = `${String(field.scrollHeight)}px`;
  }, [value, ref]);

  return (
    <div
      className={[
        compact ? "border-0 bg-transparent" : "rounded-input border border-input-edge bg-paper",
        disabled ? "opacity-70" : "",
        "transition-opacity duration-[var(--pc-dur-fast)]",
      ].join(" ")}
    >
      <label className="sr-only" htmlFor="pc-input">
        {placeholder}
      </label>
      <textarea
        id="pc-input"
        ref={ref}
        rows={1}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => { setValue(e.target.value); }}
        onKeyDown={onKeyDown}
        // Grows with the writing now that Enter makes lines (the effect
        // above), but stops well short of eating the narration it is a
        // response to: `.pc-field` holds the floor and the cap.
        className={[
          "pc-field",
          compact
            ? "block w-full resize-none border-0 bg-transparent px-2 py-0 font-sans outline-none"
            : "block w-full resize-none border-0 bg-transparent px-6 pt-5 pb-2 font-sans outline-none",
          compact ? "" : textSize,
          "text-ink placeholder:text-ink-faint",
        ].join(" ")}
      />
      <div className={compact ? "flex flex-wrap items-center gap-x-3 gap-y-1 px-2 pb-1" : "flex items-center gap-4 px-6 pb-4"}>
        {compact ? null : (
          <NotYet>
            <span className="text-nav leading-none">{PLAY.attach}</span>
          </NotYet>
        )}
        <button
          type="button"
          onClick={onOpenSettings}
          data-press={compact ? undefined : "box"}
          className={
            compact
              ? "cursor-pointer border-0 bg-transparent p-0 font-sans text-micro text-ink-soft hover:bg-marker hover:text-ink"
              : "inline-flex min-w-[13rem] cursor-pointer items-center justify-center gap-6 rounded-pill border border-ink-soft bg-transparent px-6 py-1 font-sans text-nav text-ink hover:bg-marker"
          }
        >
          <Lettering text={PLAY.settings} />
          <span aria-hidden="true">{PLAY.settingsCaret}</span>
        </button>
        {extraControl}
        <div className={compact ? "ml-auto flex items-center gap-2" : "ml-auto flex items-center gap-4"}>
          {dictate !== null && dictate !== undefined && dictation.supported ? (
            <Microphone control={dictation} disabled={disabled} />
          ) : (
            <NotYet>
              <span aria-hidden="true" className="text-nav leading-none">
                🎤
              </span>
            </NotYet>
          )}
          {/* The send control, bottom-right. Now that Enter no longer submits,
              this is the ONLY visible way to spend a day — so it is a real
              button with a real label, not an icon a learner has to guess at. */}
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label={PLAY.sendLabel}
            data-press="box"
            className={[
              compact
                ? "inline-flex cursor-pointer items-center gap-2 border px-3 py-1 font-sans text-micro"
                : "inline-flex cursor-pointer items-center gap-3 rounded-pill border px-6 py-1 font-sans text-nav",
              canSend ? "border-ink bg-ink text-paper hover:bg-ink-soft" : "cursor-not-allowed border-ink-faint bg-transparent text-ink-faint",
            ].join(" ")}
          >
            <Lettering text={PLAY.send} />
            <span aria-hidden="true">{PLAY.sendCaret}</span>
          </button>
        </div>
      </div>
      {disabled && disabledReason !== undefined ? (
        <div className={compact ? "px-2 font-sans text-micro text-ink-soft" : "border-t border-t-input-edge px-6 py-2 font-sans text-micro text-ink-soft"}>
          {disabledReason}
        </div>
      ) : null}
      <span className="sr-only">
        {PROPOSED.notYet} Enter makes a new line. Send with the send button, or with Command and Enter. Up recalls what you last said.
      </span>
    </div>
  );
}

/**
 * The live microphone. One button whose label is a plain sentence about what
 * is happening — the frames draw the icon and nothing else, so the states are
 * authored (design/copy.ts PROPOSED). Disabled with the field: nothing is
 * recorded while a turn is being worked, because there is nowhere for the
 * words to go.
 */
function Microphone({ control, disabled }: { control: DictationControl; disabled: boolean }): ReactNode {
  const busy = control.state === "transcribing" || control.state === "asking";
  const label = (() => {
    switch (control.state) {
      case "listening":
        return PROPOSED.listening;
      case "transcribing":
        return PROPOSED.transcribing;
      case "asking":
        return PROPOSED.micAsking;
      case "denied":
        return PROPOSED.micDenied;
      case "failed":
        return PROPOSED.micFailed;
      case "idle":
        return PROPOSED.speak;
    }
  })();
  return (
    <button
      type="button"
      onClick={control.toggle}
      disabled={disabled || busy}
      aria-label={PROPOSED.speakLabel}
      aria-pressed={control.state === "listening"}
      data-dictation={control.state}
      className={[
        "inline-flex items-center gap-2 rounded-pill border px-4 py-1 font-sans text-nav",
        disabled || busy ? "cursor-not-allowed border-ink-faint text-ink-faint" : "cursor-pointer hover:bg-marker",
        control.state === "listening" ? "border-ink bg-marker text-ink" : "border-transparent bg-transparent text-ink",
      ].join(" ")}
    >
      <span aria-hidden="true" className="text-nav leading-none">
        {control.state === "listening" ? "●" : "🎤"}
      </span>
      <Lettering className="text-micro tracking-wide" text={label} />
    </button>
  );
}
